import React, { useState, useEffect } from "react";
import { Database, Folder, Plus, CheckCircle2, Trash2, Loader2, Users, ExternalLink, Calculator, UserRound, Clapperboard } from "lucide-react";
import type {
  Actor,
  AppSettings,
  Casting,
  Character,
  CRMPerson,
  ProjectExternalCrewMember,
  ProjectInternalTeamMember,
  ProjectSetup,
  RateCard,
  AssetRateCard,
  Shot,
} from "../types";
import type { FptSyncStatus } from "../models/production";
import { generateProjectShowColour, getReadableTextColour, normalizeHexColour } from "../lib/projectShowColours";
import { RateCardForm } from "./RateCardForm";
import { ShotCodeGenerator } from "./ShotCodeGenerator";
import { cn, sortShots } from "../utils";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Wand2 } from "lucide-react";

const projectShowRelationshipsCache = new Map<
  string,
  {
    characters: Character[];
    actors: Actor[];
    castings: Casting[];
    internalTeamMembers: ProjectInternalTeamMember[];
    externalCrewMembers: ProjectExternalCrewMember[];
  }
>();
const projectShowRelationshipsPromiseCache = new Map<string, Promise<{
  characters: Character[];
  actors: Actor[];
  castings: Casting[];
  internalTeamMembers: ProjectInternalTeamMember[];
  externalCrewMembers: ProjectExternalCrewMember[];
}>>();
const projectShowRelationshipsCacheTime = new Map<string, number>();
const PROJECT_SHOW_RELATIONSHIPS_CACHE_VERSION = 1;
const PROJECT_SHOW_RELATIONSHIPS_FRESH_MS = 2 * 60 * 1000;
const getProjectShowRelationshipsStorageKey = (projectId: string) => `project_show_relationships_${projectId}`;
const PEOPLE_DIRECTORY_CACHE_KEY = "people_directory_cache_v1";
const PEOPLE_DIRECTORY_FRESH_MS = 5 * 60 * 1000;

const loadCachedProjectShowRelationships = (projectId?: string | null) => {
  if (!projectId || typeof window === "undefined") return undefined;
  const inMemory = projectShowRelationshipsCache.get(projectId);
  if (inMemory) return inMemory;
  try {
    const raw = window.localStorage.getItem(getProjectShowRelationshipsStorageKey(projectId));
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    if (parsed?.__cacheVersion !== PROJECT_SHOW_RELATIONSHIPS_CACHE_VERSION) return undefined;
    const hydrated = {
      characters: Array.isArray(parsed.characters) ? parsed.characters : [],
      actors: Array.isArray(parsed.actors) ? parsed.actors : [],
      castings: Array.isArray(parsed.castings) ? parsed.castings : [],
      internalTeamMembers: Array.isArray(parsed.internalTeamMembers) ? parsed.internalTeamMembers : [],
      externalCrewMembers: Array.isArray(parsed.externalCrewMembers) ? parsed.externalCrewMembers : [],
    };
    projectShowRelationshipsCache.set(projectId, hydrated);
    projectShowRelationshipsCacheTime.set(projectId, Number(parsed.__cachedAt) || 0);
    return hydrated;
  } catch {
    return undefined;
  }
};

const persistProjectShowRelationshipsCache = (
  projectId: string,
  relationships: {
    characters: Character[];
    actors: Actor[];
    castings: Casting[];
    internalTeamMembers: ProjectInternalTeamMember[];
    externalCrewMembers: ProjectExternalCrewMember[];
  }
) => {
  projectShowRelationshipsCache.set(projectId, relationships);
  projectShowRelationshipsCacheTime.set(projectId, Date.now());
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      getProjectShowRelationshipsStorageKey(projectId),
      JSON.stringify({
        __cacheVersion: PROJECT_SHOW_RELATIONSHIPS_CACHE_VERSION,
        __cachedAt: Date.now(),
        ...relationships,
      })
    );
  } catch (error) {
    console.warn("Failed to cache project show relationships locally", error);
  }
};

let peopleDirectoryCache: CRMPerson[] | null = null;
let peopleDirectoryPromise: Promise<CRMPerson[]> | null = null;
let peopleDirectoryCachedAt = 0;

interface ProjectSettingsProps {
  onBack: () => void;
  projectId?: string | null;
  setup: ProjectSetup;
  rates: RateCard;
  assetRates: AssetRateCard;
  onRatesChange: (rates: RateCard) => void;
  onAssetRatesChange: (rates: AssetRateCard) => void;
  onSetupChange: (setup: ProjectSetup) => void;
  shots: Shot[];
  onShotsChange: (shots: Shot[]) => void;
  globalSettings: AppSettings | null;
  readOnly: boolean;
  driveConnected: boolean;
  setDriveConnected: (connected: boolean) => void;
  isSettingUpDrive: boolean;
  onSetupDrive: () => Promise<void>;
  onLinkDrive: (driveId?: string, driveName?: string) => Promise<void>;
  fptLinked: boolean;
  fptProjectId?: string | null;
  fptProjectName?: string | null;
  lastPulledAt?: string;
  onFptLinkStateChange?: (metadata: {
    fptProjectId: string;
    fptProjectName?: string | null;
    fptProjectUrl?: string | null;
    fptLinked: boolean;
    syncStatus?: FptSyncStatus | null;
    lastFptSyncAt?: string | null;
  }) => void;
  onDeleteProject: () => void;
}

interface FptProjectSearchResult {
  id: number;
  name: string;
  showCode?: string;
  status?: string;
  url?: string;
}

interface FptProjectSuggestion extends FptProjectSearchResult {
  heldTimelogCount?: number;
  matchType?: "exact_name" | "normalized_name";
}

export function ProjectSettings({
  projectId,
  setup,
  rates,
  assetRates,
  onRatesChange,
  onAssetRatesChange,
  onSetupChange,
  shots,
  onShotsChange,
  globalSettings,
  readOnly,
  driveConnected,
  setDriveConnected,
  isSettingUpDrive,
  onSetupDrive,
  onLinkDrive,
  fptLinked,
  fptProjectId,
  fptProjectName,
  lastPulledAt,
  onFptLinkStateChange,
  onDeleteProject
}: ProjectSettingsProps) {
  const [activeTab, setActiveTab] = useState<"key" | "integrations" | "drive" | "cast" | "productionTeam" | "crew" | "shotcodes">("key");
  const [isSyncing, setIsSyncing] = useState(false);
  const { user } = useAuth();
  
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [newMemberRole, setNewMemberRole] = useState("writer"); // Default to Contributor
  const [addingMember, setAddingMember] = useState(false);
  const [domainUsers, setDomainUsers] = useState<any[]>([]);
  const [teamPeople, setTeamPeople] = useState<CRMPerson[]>([]);
  const [loadingTeamPeople, setLoadingTeamPeople] = useState(false);
  const [allPeople, setAllPeople] = useState<CRMPerson[]>([]);
  const cachedRelationships = loadCachedProjectShowRelationships(projectId);
  const [showRelationships, setShowRelationships] = useState<{
    characters: Character[];
    actors: Actor[];
    castings: Casting[];
    internalTeamMembers: ProjectInternalTeamMember[];
    externalCrewMembers: ProjectExternalCrewMember[];
  }>(
    cachedRelationships || {
      characters: [],
      actors: [],
      castings: [],
      internalTeamMembers: [],
      externalCrewMembers: [],
    }
  );
  const [isLoadingRelationships, setIsLoadingRelationships] = useState(false);
  const [manualCharacterName, setManualCharacterName] = useState("");
  const [castActorSearch, setCastActorSearch] = useState<Record<string, string>>({});
  const [newTeamMember, setNewTeamMember] = useState({ internalPersonId: "", roleTitle: "", department: "", displayGroup: "team" });
  const [teamPersonQuery, setTeamPersonQuery] = useState("");
  const [showTeamPersonSuggestions, setShowTeamPersonSuggestions] = useState(false);
  const [isDisconnectDriveConfirmOpen, setIsDisconnectDriveConfirmOpen] = useState(false);
  const [newCrewMember, setNewCrewMember] = useState({ externalPersonId: "", name: "", roleTitle: "", department: "" });
  const [sharedDrives, setSharedDrives] = useState<{ id: string; name: string }[]>([]);
  const [selectedDriveId, setSelectedDriveId] = useState<string>("");
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [fptSearchQuery, setFptSearchQuery] = useState("");
  const [isSearchingFpt, setIsSearchingFpt] = useState(false);
  const [isLinkingFpt, setIsLinkingFpt] = useState(false);
  const [isSyncingTaskRates, setIsSyncingTaskRates] = useState(false);
  const [fptSearchResults, setFptSearchResults] = useState<FptProjectSearchResult[]>([]);
  const [fptSuggestedProject, setFptSuggestedProject] = useState<FptProjectSuggestion | null>(null);
  const [isLoadingFptSuggestion, setIsLoadingFptSuggestion] = useState(false);
  const autocompleteRef = React.useRef<HTMLDivElement>(null);
  const teamPersonAutocompleteRef = React.useRef<HTMLDivElement>(null);

  const getPersonName = (person?: any) =>
    String(
      person?.displayName ||
      person?.fullName ||
      person?.name ||
      [person?.firstName, person?.lastName].filter(Boolean).join(" ") ||
      ""
    ).trim();

  const getPersonInitials = (person?: any, fallback = "?") => {
    const name = getPersonName(person) || fallback;
    const parts = name.split(/\s+/).filter(Boolean);
    return (parts.length > 1 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : name.slice(0, 2)).toUpperCase();
  };

  const getDisplayGroupLabel = (value?: string | null) => {
    if (value === "leadership") return "Leadership";
    if (value === "leads") return "Leads";
    return "Team";
  };

  const googleDriveLinked = Boolean(setup.driveFolderId);
  const driveSummaryText = googleDriveLinked
    ? `Linked to ${setup.driveFolderName || "Google Drive folder"}`
    : "No Google Drive folder is linked to this project yet.";
  const fptSummaryText = fptLinked
    ? fptProjectName && fptProjectId
      ? `Linked to ${fptProjectName} (${fptProjectId})`
      : `Linked to ${fptProjectName || `Flow Production Tracking project ${fptProjectId}`}`
    : "No Autodesk Flow Production Tracking project is linked yet.";
  const shouldPromptInitialRatePush = fptLinked && (!lastPulledAt || !String(lastPulledAt).trim());
  const accentColor = normalizeHexColour(setup.themeAccentColor) || generateProjectShowColour(projectId || setup.projectName || "project");
  const accentTextColor = getReadableTextColour(accentColor);
  const externalPeople = React.useMemo(
    () => allPeople.filter((person) => person.directoryType === "external" || person.personType === "external"),
    [allPeople]
  );
  const actorsById = React.useMemo(() => new Map(showRelationships.actors.map((actor) => [actor.id, actor])), [showRelationships.actors]);
  const castingsByCharacterId = React.useMemo(
    () => new Map(showRelationships.castings.map((casting) => [casting.characterId, casting])),
    [showRelationships.castings]
  );
  const characterSourceSummary = React.useMemo(() => {
    const summary = {
      script: 0,
      shootingSchedule: 0,
      manual: 0,
    };

    for (const character of showRelationships.characters) {
      if (character.source === "script") {
        summary.script += 1;
      } else if (character.source === "shooting_schedule") {
        summary.shootingSchedule += 1;
      } else {
        summary.manual += 1;
      }
    }

    return summary;
  }, [showRelationships.characters]);
  const teamPersonSuggestions = React.useMemo(() => {
    const query = teamPersonQuery.trim().toLowerCase();
    if (!query) return teamPeople.slice(0, 8);
    return teamPeople
      .filter((person) => {
        const name = String(person.name || person.displayName || person.fullName || "").toLowerCase();
        const email = String(person.email || person.googleWorkspaceEmail || "").toLowerCase();
        return name.includes(query) || email.includes(query);
      })
      .sort((a, b) => {
        const aName = String(a.name || a.displayName || a.fullName || "").toLowerCase();
        const bName = String(b.name || b.displayName || b.fullName || "").toLowerCase();
        const aStarts = aName.startsWith(query) ? 0 : 1;
        const bStarts = bName.startsWith(query) ? 0 : 1;
        return aStarts - bStarts || aName.localeCompare(bName);
      })
      .slice(0, 8);
  }, [teamPeople, teamPersonQuery]);

  const selectTeamPerson = (person: CRMPerson) => {
    setNewTeamMember((prev) => ({
      ...prev,
      internalPersonId: person.id,
      roleTitle: person.roleTitle || person.title || person.role || "",
      department: person.department || "",
    }));
    setTeamPersonQuery(person.name || person.displayName || person.fullName || "");
    setShowTeamPersonSuggestions(false);
  };

  const fetchShowRelationships = async (options?: { force?: boolean }) => {
    if (!user || !projectId) return;
    const cached = loadCachedProjectShowRelationships(projectId);
    const cachedAt = projectShowRelationshipsCacheTime.get(projectId) || 0;
    const isCacheFresh = cached && Date.now() - cachedAt < PROJECT_SHOW_RELATIONSHIPS_FRESH_MS;
    if (cached) {
      setShowRelationships(cached);
    }
    if (isCacheFresh && !options?.force) {
      setIsLoadingRelationships(false);
      return;
    }
    if (!cached) {
      setIsLoadingRelationships(true);
    }
    if (!options?.force && projectShowRelationshipsPromiseCache.has(projectId)) {
      try {
        const inFlight = await projectShowRelationshipsPromiseCache.get(projectId)!;
        setShowRelationships(inFlight);
      } finally {
        setIsLoadingRelationships(false);
      }
      return;
    }

    const request = (async () => {
      const res = await fetch(`/api/projects/${projectId}/show-dashboard`, {
        headers: { "x-user-uid": user.uid },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Failed to load project show relationships.");
      return {
        characters: Array.isArray(data.characters) ? data.characters : [],
        actors: Array.isArray(data.actors) ? data.actors : [],
        castings: Array.isArray(data.castings) ? data.castings : [],
        internalTeamMembers: Array.isArray(data.internalTeamMembers) ? data.internalTeamMembers : [],
        externalCrewMembers: Array.isArray(data.externalCrewMembers) ? data.externalCrewMembers : [],
      };
    })();
    projectShowRelationshipsPromiseCache.set(projectId, request);
    try {
      const nextRelationships = await request;
      persistProjectShowRelationshipsCache(projectId, nextRelationships);
      setShowRelationships(nextRelationships);
    } catch (error: any) {
      if (!cached) {
        toast.error(error.message || "Failed to load project show relationships.");
      }
    } finally {
      projectShowRelationshipsPromiseCache.delete(projectId);
      setIsLoadingRelationships(false);
    }
  };

  const fetchTeamMembers = async (retries = 3) => {
    if (!setup.driveFolderId || !user) return;
    setLoadingTeam(true);
    try {
      const res = await fetch(`/api/drive/permissions?folderId=${setup.driveFolderId}`, {
        headers: { "x-user-uid": user.uid }
      });
      if (res.ok) {
        const data = await res.json();
        setTeamMembers(data.permissions || []);
      }
    } catch (error) {
      if (retries > 0) {
        setTimeout(() => fetchTeamMembers(retries - 1), 2000);
      } else {
        console.error("Failed to fetch team members:", error);
        toast.error("Failed to load team access");
      }
    } finally {
      if (retries === 0 || !loadingTeam) {
        setLoadingTeam(false);
      }
    }
  };

  const fetchDomainUsers = async (retries = 3) => {
    if (!user) return;
    try {
      const res = await fetch("/api/crm/people", {
        headers: { "x-user-uid": user.uid }
      });
      if (res.ok) {
        const data = await res.json();
        setAllPeople(data);
        setDomainUsers(
          data.filter((p: any) =>
            p.directoryType === "internal" ||
            p.personType === "staff" ||
            p.workspaceAccountStatus === "active" ||
            p.workspaceAccountStatus === "inactive" ||
            p.id.startsWith("workspace-") ||
            p.id.startsWith("user-")
          )
        );
      }
    } catch (error) {
      if (retries > 0) {
        setTimeout(() => fetchDomainUsers(retries - 1), 2000);
      } else {
        console.error("Failed to fetch domain users:", error);
      }
    }
  };

  const fetchTeamPeople = async (retries = 3) => {
    if (!user) return;
    if (!peopleDirectoryCache && typeof window !== "undefined") {
      try {
        const raw = window.localStorage.getItem(PEOPLE_DIRECTORY_CACHE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed?.items) && Date.now() - Number(parsed.cachedAt || 0) < PEOPLE_DIRECTORY_FRESH_MS) {
            peopleDirectoryCache = parsed.items;
            peopleDirectoryCachedAt = Number(parsed.cachedAt || 0);
          }
        }
      } catch {
        // Ignore local cache parse issues and fall back to network.
      }
    }
    if (peopleDirectoryCache && Date.now() - peopleDirectoryCachedAt < PEOPLE_DIRECTORY_FRESH_MS) {
      setTeamPeople(peopleDirectoryCache);
      return;
    }
    setLoadingTeamPeople(true);
    try {
      if (!peopleDirectoryPromise) {
        peopleDirectoryPromise = fetch("/api/people", {
          headers: { "x-user-uid": user.uid }
        }).then(async (res) => {
          if (!res.ok) return [];
          const data = await res.json();
          return Array.isArray(data) ? data : [];
        });
      }
      const people = await peopleDirectoryPromise;
      peopleDirectoryCache = people;
      peopleDirectoryCachedAt = Date.now();
      if (typeof window !== "undefined") {
        try {
          window.localStorage.setItem(
            PEOPLE_DIRECTORY_CACHE_KEY,
            JSON.stringify({ cachedAt: peopleDirectoryCachedAt, items: people })
          );
        } catch (error) {
          console.warn("Failed to cache People / HR directory locally", error);
        }
      }
      setTeamPeople(people);
    } catch (error) {
      if (retries > 0) {
        setTimeout(() => fetchTeamPeople(retries - 1), 2000);
      } else {
        console.error("Failed to fetch People / HR records for project team linking:", error);
      }
    } finally {
      peopleDirectoryPromise = null;
      setLoadingTeamPeople(false);
    }
  };

  const postRelationship = async (path: string, body: unknown, method = "POST") => {
    if (!user || !projectId) return null;
    const res = await fetch(`/api/projects/${projectId}/show-dashboard/${path}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        "x-user-uid": user.uid,
      },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || "Failed to save relationship.");
    const nextRelationships = {
      characters: Array.isArray(data.characters) ? data.characters : [],
      actors: Array.isArray(data.actors) ? data.actors : [],
      castings: Array.isArray(data.castings) ? data.castings : [],
      internalTeamMembers: Array.isArray(data.internalTeamMembers) ? data.internalTeamMembers : [],
      externalCrewMembers: Array.isArray(data.externalCrewMembers) ? data.externalCrewMembers : [],
    };
      if (projectId) {
        persistProjectShowRelationshipsCache(projectId, nextRelationships);
      }
      setShowRelationships(nextRelationships);
      return data;
  };

  const handleAddManualCharacter = async () => {
    if (!manualCharacterName.trim()) return;
    try {
      await postRelationship("characters", { name: manualCharacterName.trim(), source: "manual" });
      setManualCharacterName("");
      toast.success("Character added");
    } catch (error: any) {
      toast.error(error.message || "Failed to add character");
    }
  };

  const handleImportExtractedCharacters = async () => {
    try {
      await postRelationship("characters/import", {});
      toast.success("Extracted characters synced");
    } catch (error: any) {
      toast.error(error.message || "Failed to sync extracted characters");
    }
  };

  const deleteRelationship = async (path: string) => {
    if (!user || !projectId) return;
    try {
      const res = await fetch(`/api/projects/${projectId}/show-dashboard/${path}`, {
        method: "DELETE",
        headers: { "x-user-uid": user.uid },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Failed to remove relationship.");
      const nextRelationships = {
        characters: Array.isArray(data.characters) ? data.characters : [],
        actors: Array.isArray(data.actors) ? data.actors : [],
        castings: Array.isArray(data.castings) ? data.castings : [],
        internalTeamMembers: Array.isArray(data.internalTeamMembers) ? data.internalTeamMembers : [],
        externalCrewMembers: Array.isArray(data.externalCrewMembers) ? data.externalCrewMembers : [],
      };
      if (projectId) {
        persistProjectShowRelationshipsCache(projectId, nextRelationships);
      }
      setShowRelationships(nextRelationships);
      toast.success("Relationship removed");
    } catch (error: any) {
      toast.error(error.message || "Failed to remove relationship");
    }
  };

  const handleSaveCharacter = async (character: Character) => {
    try {
      await postRelationship(`characters/${character.id}`, character, "PUT");
      toast.success("Character saved");
    } catch (error: any) {
      toast.error(error.message || "Failed to save character");
    }
  };

  const handleLinkActor = async (character: Character) => {
    const typedActorName = (castActorSearch[character.id] || "").trim();
    if (!typedActorName) return;
    try {
      let actor = showRelationships.actors.find((item) => item.name.toLowerCase() === typedActorName.toLowerCase());
      if (!actor) {
        const data = await postRelationship("actors", { name: typedActorName });
        actor = data?.actors?.find((item: Actor) => item.name.toLowerCase() === typedActorName.toLowerCase());
      }
      if (!actor) throw new Error("Actor was not created.");
      const currentCasting = castingsByCharacterId.get(character.id);
      await postRelationship("castings", {
        characterId: character.id,
        actorId: actor.id,
        castingStatus: "confirmed",
        isMainCast: currentCasting?.isMainCast || false,
        displayOrder: currentCasting?.displayOrder ?? character.displayOrder,
        notes: currentCasting?.notes || "",
      });
      toast.success("Actor linked");
    } catch (error: any) {
      toast.error(error.message || "Failed to link actor");
    }
  };

  const handleSaveActor = async (actor: Actor) => {
    try {
      await postRelationship("actors", actor);
      toast.success("Actor calling card saved");
    } catch (error: any) {
      toast.error(error.message || "Failed to save actor calling card");
    }
  };

  const handleToggleMainCast = async (character: Character, checked: boolean) => {
    try {
      const currentCasting = castingsByCharacterId.get(character.id);
      await postRelationship("castings", {
        characterId: character.id,
        actorId: currentCasting?.actorId || null,
        castingStatus: currentCasting?.castingStatus || "unassigned",
        isMainCast: checked,
        displayOrder: currentCasting?.displayOrder ?? character.displayOrder,
        notes: currentCasting?.notes || "",
      });
    } catch (error: any) {
      toast.error(error.message || "Failed to update main cast flag");
    }
  };

  const handleAddProductionTeamMember = async () => {
    if (!newTeamMember.internalPersonId) return;
    try {
      await postRelationship("internal-team", newTeamMember);
      setNewTeamMember({ internalPersonId: "", roleTitle: "", department: "", displayGroup: "team" });
      setTeamPersonQuery("");
      setShowTeamPersonSuggestions(false);
      toast.success("Team member linked");
    } catch (error: any) {
      toast.error(error.message || "Failed to link team member");
    }
  };

  const handleUpdateProductionTeamMember = async (
    member: ProjectInternalTeamMember,
    updates: Partial<Pick<ProjectInternalTeamMember, "roleTitle" | "department" | "displayGroup" | "isActive">>
  ) => {
    if (!member.internalPersonId) return;
    try {
      await postRelationship("internal-team", {
        internalPersonId: member.internalPersonId,
        roleTitle: updates.roleTitle ?? member.roleTitle ?? null,
        department: updates.department ?? member.department ?? null,
        displayGroup: updates.displayGroup ?? member.displayGroup ?? "team",
        displayOrder: member.displayOrder,
        isActive: updates.isActive ?? member.isActive ?? true,
      });
      toast.success("Team member updated");
    } catch (error: any) {
      toast.error(error.message || "Failed to update team member");
    }
  };

  const handleAddExternalCrewMember = async () => {
    const typedName = newCrewMember.name.trim();
    const selectedPerson = externalPeople.find((person) => person.id === newCrewMember.externalPersonId)
      || externalPeople.find((person) => typedName && person.name?.trim().toLowerCase() === typedName.toLowerCase());
    if (!selectedPerson && !typedName) return;
    try {
      await postRelationship("external-crew", {
        externalPersonId: selectedPerson?.id || "",
        roleTitle: newCrewMember.roleTitle,
        department: newCrewMember.department,
        companyId: selectedPerson?.companyId || "",
        person: selectedPerson ? undefined : {
          name: newCrewMember.name.trim(),
          roleTitle: newCrewMember.roleTitle,
          department: newCrewMember.department,
        },
      });
      setNewCrewMember({ externalPersonId: "", name: "", roleTitle: "", department: "" });
      fetchDomainUsers();
      toast.success("Crew member linked");
    } catch (error: any) {
      toast.error(error.message || "Failed to link crew member");
    }
  };

  const handleSaveExternalCrewMember = async (member: ProjectExternalCrewMember) => {
    try {
      await postRelationship("external-crew", member);
      toast.success("Crew calling card saved");
    } catch (error: any) {
      toast.error(error.message || "Failed to save crew calling card");
    }
  };

  const fetchSharedDrives = async (retries = 3) => {
    if (!user || !driveConnected) return;
    try {
      const res = await fetch("/api/drive/shared-drives", {
        headers: { "x-user-uid": user.uid }
      });
      const data = await res.json();
      if (Array.isArray(data.drives)) {
        const uniqueDrives = Array.from(new Map(data.drives.map((drive: any) => [drive.id, drive])).values()) as { id: string; name: string }[];
        setSharedDrives(uniqueDrives);
        if (uniqueDrives.length > 0 && !selectedDriveId) {
          setSelectedDriveId(uniqueDrives[0].id);
        }
      }
    } catch (error) {
      if (retries > 0) {
        setTimeout(() => fetchSharedDrives(retries - 1), 2000);
      } else {
        console.error("Failed to fetch shared drives:", error);
      }
    }
  };

  const handleConnectGoogleDrive = async () => {
    if (!user) return;
    try {
      const res = await fetch(`/api/auth/google/url?uid=${user.uid}`);
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error || "Failed to get auth URL");
        return;
      }
      window.open(data.url, "google_auth", "width=600,height=700");

      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type === "OAUTH_AUTH_SUCCESS") {
          setDriveConnected(true);
          fetchSharedDrives();
          window.removeEventListener("message", handleMessage);
          toast.success("Google Drive connected");
        }
      };
      window.addEventListener("message", handleMessage);
    } catch (error) {
      console.error("Failed to get auth URL", error);
      toast.error("Failed to connect to Google Drive");
    }
  };

  const handleDisconnectProjectDrive = () => {
    const {
      driveFolderId: _driveFolderId,
      driveFolderName: _driveFolderName,
      driveSubfolders: _driveSubfolders,
      driveThemeId: _driveThemeId,
      ...setupWithoutDrive
    } = setup;

    onSetupChange(setupWithoutDrive as ProjectSetup);
    setSelectedDriveId("");
    setIsDisconnectDriveConfirmOpen(false);
    toast.success("Project Google Drive disconnected");
  };

  const handleSearchFptProjects = async () => {
    if (!user || !projectId) return;
    const trimmed = fptSearchQuery.trim();
    if (trimmed.length < 2) {
      toast.error("Enter at least 2 characters to search Flow Production Tracking projects.");
      return;
    }

    setIsSearchingFpt(true);
    try {
      const params = new URLSearchParams({
        projectId,
        q: trimmed,
      });
      const res = await fetch(`/api/fpt/projects/search?${params.toString()}`, {
        headers: { "x-user-uid": user.uid },
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to search Flow Production Tracking projects.");
      }
      setFptSearchResults(Array.isArray(data.results) ? data.results : []);
      if (!data.results?.length) {
        toast.message("No matching Flow Production Tracking projects found.");
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to search Flow Production Tracking projects.");
    } finally {
      setIsSearchingFpt(false);
    }
  };

  const handleLinkExistingFptProject = async (candidate: FptProjectSearchResult) => {
    if (!user || !projectId) return;
    setIsLinkingFpt(true);
    try {
      const res = await fetch(`/api/fpt/projects/${projectId}/link-existing`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid,
        },
        body: JSON.stringify({ fptProjectId: candidate.id }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to link the existing Flow Production Tracking project.");
      }

      onFptLinkStateChange?.(data.linkedProject);
      setFptSearchResults([]);
      setFptSearchQuery("");
      toast.success(data.message || "Existing Flow Production Tracking project linked.", {
        description: "Next, push task rates so Flow Production Tracking can populate sg_rate and calculate bid cost nightly.",
      });
    } catch (error: any) {
      toast.error(error.message || "Failed to link the existing Flow Production Tracking project.");
    } finally {
      setIsLinkingFpt(false);
    }
  };

  const handleSyncTaskRatesToFpt = async () => {
    if (!user || !projectId || !fptLinked) return;
    setIsSyncingTaskRates(true);
    try {
      const res = await fetch(`/api/fpt/projects/${projectId}/sync/tasks`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid,
        },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to sync task rate, bid, and duration data to Flow Production Tracking.");
      }
      const syncedCount = Array.isArray(data.result) ? data.result.length : 0;
      const changedCount = Array.isArray(data.result) ? data.result.filter((item: any) => item?.changed).length : 0;
      const supervisionWasChecked = Boolean(data.projectSupervisionTaskResult);
      const supervisionChanged = Boolean(data.projectSupervisionTaskResult?.changed);
      const skippedUnsyncedParentTaskCount = Number(data.skippedUnsyncedParentTaskCount || 0);
      toast.success("Pushed task rate, bid, and duration data to Flow Production Tracking.", {
        description: changedCount > 0
          ? `${changedCount} task${changedCount === 1 ? "" : "s"} updated in Flow Production Tracking.${supervisionWasChecked ? ` Combined supervision task ${supervisionChanged ? "created or updated" : "checked"} too.` : ""}${skippedUnsyncedParentTaskCount > 0 ? ` ${skippedUnsyncedParentTaskCount} task${skippedUnsyncedParentTaskCount === 1 ? "" : "s"} were skipped because their parent shot or asset is not linked in Flow Production Tracking yet.` : ""} Flow Production Tracking can now calculate sg_bid_cost from est_in_mins × sg_rate.`
          : `${syncedCount} task${syncedCount === 1 ? "" : "s"} checked with no rate changes needed.${supervisionWasChecked ? ` Combined supervision task ${supervisionChanged ? "created or updated" : "checked"} too.` : ""}${skippedUnsyncedParentTaskCount > 0 ? ` ${skippedUnsyncedParentTaskCount} task${skippedUnsyncedParentTaskCount === 1 ? "" : "s"} were skipped because their parent shot or asset is not linked in Flow Production Tracking yet.` : ""}`,
      });
    } catch (error: any) {
      toast.error(error.message || "Failed to sync task rate, bid, and duration data to Flow Production Tracking.");
    } finally {
      setIsSyncingTaskRates(false);
    }
  };

  useEffect(() => {
    if (!projectId) return;
    const cached = loadCachedProjectShowRelationships(projectId);
    if (cached) {
      setShowRelationships(cached);
    }
  }, [projectId]);

  useEffect(() => {
    if (activeTab === "drive" && driveConnected && setup.driveFolderId) {
      fetchTeamMembers();
    }
    if (activeTab === "drive" || activeTab === "cast" || activeTab === "productionTeam" || activeTab === "crew") {
      fetchDomainUsers();
    }
    if (activeTab === "productionTeam") {
      fetchTeamPeople();
    }
    if (activeTab === "integrations" && driveConnected) {
      fetchSharedDrives();
    }
  }, [activeTab, driveConnected, setup.driveFolderId]);

  useEffect(() => {
    if (projectId && user?.uid) {
      fetchShowRelationships();
    }
  }, [projectId, user?.uid]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target as Node)) {
        setShowAutocomplete(false);
      }
      if (teamPersonAutocompleteRef.current && !teamPersonAutocompleteRef.current.contains(event.target as Node)) {
        setShowTeamPersonSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (activeTab !== "integrations" || fptLinked || !user || !projectId) {
      setFptSuggestedProject(null);
      return;
    }

    let cancelled = false;
    setIsLoadingFptSuggestion(true);
    fetch(`/api/fpt/projects/${projectId}/suggest-link`, {
      headers: { "x-user-uid": user.uid },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || "Failed to load Flow Production Tracking project suggestion.");
        }
        if (!cancelled) {
          setFptSuggestedProject(data.suggestion || null);
        }
      })
      .catch((error: any) => {
        if (!cancelled) {
          console.error("Failed to load suggested Flow Production Tracking project", error);
          setFptSuggestedProject(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingFptSuggestion(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeTab, fptLinked, projectId, user]);

  const handleAddMember = async () => {
    if (!newMemberEmail || !setup.driveFolderId || !user) return;
    setAddingMember(true);
    try {
      const res = await fetch(`/api/drive/assign-user`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "x-user-uid": user.uid 
        },
        body: JSON.stringify({
          folderId: setup.driveFolderId,
          email: newMemberEmail,
          role: newMemberRole
        })
      });
      
      if (res.ok) {
        toast.success("Team member added");
        setNewMemberEmail("");
        fetchTeamMembers();
      } else {
        const data = await res.json();
        toast.error(data.error || "Failed to add team member");
      }
    } catch (error) {
      console.error("Failed to add member:", error);
      toast.error("An error occurred");
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (permissionId: string) => {
    if (!setup.driveFolderId || !user) return;
    try {
      const res = await fetch(`/api/drive/permissions/${permissionId}?folderId=${setup.driveFolderId}`, {
        method: "DELETE",
        headers: { "x-user-uid": user.uid }
      });
      
      if (res.ok) {
        toast.success("Team member removed");
        fetchTeamMembers();
      } else {
        toast.error("Failed to remove team member");
      }
    } catch (error) {
      console.error("Failed to remove member:", error);
      toast.error("An error occurred");
    }
  };

  const handleUpdateRole = async (permissionId: string, newRole: string) => {
    if (!setup.driveFolderId || !user) return;
    try {
      const res = await fetch(`/api/drive/permissions/${permissionId}`, {
        method: "PATCH",
        headers: { 
          "Content-Type": "application/json",
          "x-user-uid": user.uid 
        },
        body: JSON.stringify({
          folderId: setup.driveFolderId,
          role: newRole
        })
      });
      
      if (res.ok) {
        toast.success("Role updated");
        fetchTeamMembers();
      } else {
        toast.error("Failed to update role");
      }
    } catch (error) {
      console.error("Failed to update role:", error);
      toast.error("An error occurred");
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="mb-6 bg-white dark:bg-slate-900 px-4 py-4 rounded-lg border border-slate-200 dark:border-slate-800 flex items-center justify-between shadow-sm shrink-0">
        <div className="flex items-center gap-6">
          <div className="flex flex-col justify-center min-w-0">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 leading-tight truncate">
              Project Settings
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
              Manage project configuration and access
            </p>
          </div>
        </div>
        <button
          onClick={onDeleteProject}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-red-100"
        >
          <Trash2 className="w-3.5 h-3.5" />
          Delete Project
        </button>
      </div>

      <div className="mb-6 shrink-0 border-b border-slate-200 dark:border-slate-800">
        <nav className="flex space-x-8" aria-label="Tabs">
          {[
            { id: 'key', label: 'Key', icon: Database },
            { id: 'integrations', label: 'Integrations', icon: Folder },
            { id: 'drive', label: 'Drive Access', icon: Users },
            { id: 'cast', label: 'Cast', icon: Clapperboard },
            { id: 'productionTeam', label: 'Team', icon: UserRound },
            { id: 'crew', label: 'Crew', icon: ExternalLink },
            { id: 'shotcodes', label: 'Shot Codes', icon: Wand2 },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`
                  whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2
                  ${activeTab === tab.id
                    ? 'border-[#ffcc01] text-[#ffcc01] dark:text-[#ffcc01]'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300 dark:text-slate-500 dark:hover:text-slate-300 dark:hover:border-slate-700'}
                `}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="flex-1 overflow-auto">
        {activeTab === 'key' && (
          <div className="space-y-6">
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="px-6 py-4">
                <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Key</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Define standard rates and parameters for shot and asset based bidding</p>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Project show colour</h3>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                    Used as this project’s visual accent across show dashboard views.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={accentColor}
                    disabled={readOnly}
                    onChange={(event) => onSetupChange({ ...setup, themeAccentColor: event.target.value, themeAccentColorSource: "manual" })}
                    className="h-10 w-14 rounded-lg border border-slate-300 bg-white p-1 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950"
                    title="Project show colour"
                  />
                  <div
                    className="min-w-[150px] rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold shadow-sm dark:border-slate-700"
                    style={{ backgroundColor: accentColor, color: accentTextColor }}
                  >
                    {accentColor}
                    <span className="ml-2 text-[10px] uppercase tracking-wider opacity-75">{setup.themeAccentColorSource || "auto"}</span>
                  </div>
                  <button
                    type="button"
                    disabled={readOnly}
                    onClick={() => onSetupChange({ ...setup, themeAccentColor: generateProjectShowColour(`${projectId || ""}-${Date.now()}`), themeAccentColorSource: "auto" })}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    Regenerate
                  </button>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
              <div className="xl:col-span-3 space-y-8">
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                  <RateCardForm 
                    rates={rates} 
                    assetRates={assetRates}
                    currency={setup.currency} 
                    onChange={onRatesChange} 
                    onAssetRatesChange={onAssetRatesChange}
                    readOnly={readOnly}
                    globalDefaultRates={globalSettings?.defaultRates}
                    globalDefaultAssetRates={globalSettings?.defaultAssetRates}
                    showOnly="shots"
                  />
                </div>
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                  <RateCardForm 
                    rates={rates} 
                    assetRates={assetRates}
                    currency={setup.currency} 
                    onChange={onRatesChange} 
                    onAssetRatesChange={onAssetRatesChange}
                    readOnly={readOnly}
                    globalDefaultRates={globalSettings?.defaultRates}
                    globalDefaultAssetRates={globalSettings?.defaultAssetRates}
                    showOnly="assets"
                  />
                </div>
              </div>
              <div className="xl:col-span-1 space-y-8">
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                  <RateCardForm 
                    rates={rates} 
                    assetRates={assetRates}
                    currency={setup.currency} 
                    onChange={onRatesChange} 
                    onAssetRatesChange={onAssetRatesChange}
                    readOnly={readOnly}
                    globalDefaultRates={globalSettings?.defaultRates}
                    globalDefaultAssetRates={globalSettings?.defaultAssetRates}
                    showOnly="misc"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'drive' && (
          <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <h3 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">Team Access (Shared Drive)</h3>
            
            {!driveConnected ? (
              <div className="py-8 text-center text-slate-500 dark:text-slate-400">
                Please connect Google Drive to manage team access.
              </div>
            ) : !setup.driveFolderId ? (
              <div className="py-8 text-center text-slate-500 dark:text-slate-400">
                No Shared Drive is associated with this project.
              </div>
            ) : (
              <div className="space-y-6">
                <div className="flex items-end gap-4 rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/60">
                  <div className="min-w-0 flex-[1.35] relative" ref={autocompleteRef}>
                    <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">Email Address</label>
                    <input 
                      type="email" 
                      value={newMemberEmail}
                      onChange={(e) => {
                        setNewMemberEmail(e.target.value);
                        setShowAutocomplete(true);
                      }}
                      onFocus={() => setShowAutocomplete(true)}
                      placeholder="user@example.com"
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                    />
                    {showAutocomplete && newMemberEmail && (
                      <div className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900">
                        {domainUsers
                          .filter(u => u.email?.toLowerCase().includes(newMemberEmail.toLowerCase()) || u.name?.toLowerCase().includes(newMemberEmail.toLowerCase()))
                          .map(u => (
                            <button
                              key={u.id}
                              type="button"
                              className="flex w-full items-center gap-3 px-4 py-2 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-800"
                              onClick={() => {
                                setNewMemberEmail(u.email || '');
                                setShowAutocomplete(false);
                              }}
                            >
                              {u.photoUrl ? (
                                <img src={u.photoUrl} alt="" className="w-6 h-6 rounded-full" referrerPolicy="no-referrer" />
                              ) : (
                                <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold text-xs shrink-0">
                                  {u.name?.charAt(0) || u.email?.charAt(0) || '?'}
                                </div>
                              )}
                              <div className="min-w-0">
                                <div className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">{u.name}</div>
                                <div className="truncate text-xs text-slate-500 dark:text-slate-400">{u.email}</div>
                              </div>
                            </button>
                          ))}
                        {domainUsers.filter(u => u.email?.toLowerCase().includes(newMemberEmail.toLowerCase()) || u.name?.toLowerCase().includes(newMemberEmail.toLowerCase())).length === 0 && (
                          <div className="px-4 py-3 text-center text-sm text-slate-500 dark:text-slate-400">
                            No matching users found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="w-[340px] shrink-0">
                    <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">Role</label>
                    <select
                      value={newMemberRole}
                      onChange={(e) => setNewMemberRole(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                    >
                      <option value="commenter">Commenter</option>
                      <option value="writer">Contributor (Add, edit, share)</option>
                      <option value="fileOrganizer">Content manager (Add, edit, move, delete)</option>
                      <option value="organizer">Manager (Manage content, people, settings)</option>
                    </select>
                  </div>
                  <button
                    onClick={handleAddMember}
                    disabled={addingMember || !newMemberEmail}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium text-sm flex items-center gap-2 disabled:opacity-50"
                  >
                    {addingMember ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    Add User
                  </button>
                </div>

                {loadingTeam ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
                    <table className="w-full text-left text-sm">
                      <thead className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
                        <tr>
                          <th className="px-4 py-3 font-medium text-slate-700">User</th>
                          <th className="px-4 py-3 font-medium text-slate-700">Role</th>
                          <th className="px-4 py-3 font-medium text-slate-700 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {teamMembers.map((member) => (
                          <tr key={member.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                {member.photoLink ? (
                                  <img src={member.photoLink} alt="" className="w-8 h-8 rounded-full" referrerPolicy="no-referrer" />
                                ) : (
                                  <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold">
                                    {member.displayName?.charAt(0) || member.emailAddress?.charAt(0) || '?'}
                                  </div>
                                )}
                                <div>
                                  <div className="font-medium text-slate-900 dark:text-slate-100">{member.displayName || 'Unknown User'}</div>
                                  <div className="text-xs text-slate-500 dark:text-slate-400">{member.emailAddress}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <select
                                value={member.role}
                                onChange={(e) => handleUpdateRole(member.id, e.target.value)}
                                className="rounded border border-slate-200 bg-white px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                              >
                                <option value="reader">Viewer</option>
                                <option value="commenter">Commenter</option>
                                <option value="writer">Contributor</option>
                                <option value="fileOrganizer">Content manager</option>
                                <option value="organizer">Manager</option>
                              </select>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <button
                                onClick={() => handleRemoveMember(member.id)}
                                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="Remove Access"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                        {teamMembers.length === 0 && (
                          <tr>
                            <td colSpan={3} className="px-4 py-8 text-center text-slate-500">
                              No team members found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'cast' && (
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Cast</h3>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Cast comes from script and schedule extraction, with manual edits preserved on reimport.
                  </p>
                  <p className="mt-2 text-sm font-medium text-slate-600 dark:text-slate-300">
                    {characterSourceSummary.script} from scripts • {characterSourceSummary.shootingSchedule} from schedules
                    {characterSourceSummary.manual > 0 ? ` • ${characterSourceSummary.manual} manual` : ""}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={handleImportExtractedCharacters}
                    disabled={readOnly || isLoadingRelationships}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200"
                  >
                    <Clapperboard className="h-4 w-4" />
                    Sync extracted cast
                  </button>
                  <input
                    value={manualCharacterName}
                    onChange={(event) => setManualCharacterName(event.target.value)}
                    placeholder="Add character"
                    disabled={readOnly}
                    className="w-56 rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#ffcc01] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                  />
                  <button
                    type="button"
                    onClick={handleAddManualCharacter}
                    disabled={readOnly || !manualCharacterName.trim()}
                    className="inline-flex items-center gap-2 rounded-lg bg-[#ffcc01] px-3 py-2 text-sm font-semibold text-slate-900 disabled:opacity-50"
                  >
                    <Plus className="h-4 w-4" />
                    Add
                  </button>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
              {isLoadingRelationships ? (
                <div className="flex items-center justify-center gap-2 py-10 text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading cast relationships
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead className="border-b border-slate-200 bg-slate-50 text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:border-slate-800 dark:bg-slate-950/50">
                      <tr>
                        <th className="px-4 py-3">Cast</th>
                        <th className="px-4 py-3">Source</th>
                        <th className="px-4 py-3">Importance</th>
                        <th className="px-4 py-3">Actor</th>
                        <th className="px-4 py-3">Main</th>
                        <th className="px-4 py-3">Order</th>
                        <th className="px-4 py-3">Notes</th>
                        <th className="px-4 py-3 text-right">Save</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {showRelationships.characters.map((character) => {
                        const casting = castingsByCharacterId.get(character.id);
                        const actor = casting?.actorId ? actorsById.get(casting.actorId) : null;
                        return (
                          <tr key={character.id} className="align-top">
                            <td className="px-4 py-3">
                              <input
                                value={character.name}
                                disabled={readOnly}
                                onChange={(event) => setShowRelationships((prev) => ({
                                  ...prev,
                                  characters: prev.characters.map((item) => item.id === character.id ? { ...item, name: event.target.value } : item)
                                }))}
                                className="w-44 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                              />
                            </td>
                            <td className="px-4 py-3 capitalize text-slate-500 dark:text-slate-400">{character.source.replace("_", " ")}</td>
                            <td className="px-4 py-3">
                              <select
                                value={character.importance}
                                disabled={readOnly}
                                onChange={(event) => setShowRelationships((prev) => ({
                                  ...prev,
                                  characters: prev.characters.map((item) => item.id === character.id ? { ...item, importance: event.target.value as Character["importance"] } : item)
                                }))}
                                className="rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                              >
                                <option value="unknown">Unknown</option>
                                <option value="main">Main</option>
                                <option value="supporting">Supporting</option>
                                <option value="minor">Minor</option>
                              </select>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex min-w-64 flex-col gap-2">
                                <div className="text-xs font-semibold text-slate-600 dark:text-slate-300">{actor?.name || "No actor linked"}</div>
                                <div className="flex gap-2">
                                  <input
                                    list="project-actor-options"
                                    value={castActorSearch[character.id] || ""}
                                    disabled={readOnly}
                                    onChange={(event) => setCastActorSearch((prev) => ({ ...prev, [character.id]: event.target.value }))}
                                    placeholder="Search or create actor"
                                    className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleLinkActor(character)}
                                    disabled={readOnly || !castActorSearch[character.id]?.trim()}
                                    className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200"
                                  >
                                    Link
                                  </button>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={Boolean(casting?.isMainCast)}
                                disabled={readOnly}
                                onChange={(event) => handleToggleMainCast(character, event.target.checked)}
                                className="h-4 w-4 accent-[#ffcc01]"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                type="number"
                                value={character.displayOrder}
                                disabled={readOnly}
                                onChange={(event) => setShowRelationships((prev) => ({
                                  ...prev,
                                  characters: prev.characters.map((item) => item.id === character.id ? { ...item, displayOrder: Number(event.target.value) || 0 } : item)
                                }))}
                                className="w-20 rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <textarea
                                value={character.notes || ""}
                                disabled={readOnly}
                                onChange={(event) => setShowRelationships((prev) => ({
                                  ...prev,
                                  characters: prev.characters.map((item) => item.id === character.id ? { ...item, notes: event.target.value } : item)
                                }))}
                                className="h-16 w-60 rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                              />
                            </td>
                            <td className="px-4 py-3 text-right">
                              <button
                                type="button"
                                disabled={readOnly}
                                onClick={() => handleSaveCharacter(character)}
                                className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900"
                              >
                                Save
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                      {showRelationships.characters.length === 0 && (
                        <tr>
                          <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                            No cast yet. Analyse scripts or schedules, or add one manually.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <datalist id="project-actor-options">
                    {showRelationships.actors.map((actor) => (
                      <option key={actor.id} value={actor.name} />
                    ))}
                  </datalist>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Actor calling cards</h3>
                  <p className="mt-1 max-w-3xl text-sm text-slate-500 dark:text-slate-400">
                    Curated production/agent managed profiles only. Do not paste IMDb text or copyrighted bios; use manually approved copy and imagery.
                  </p>
                </div>
              </div>
              <div className="mt-5 space-y-4">
                {showRelationships.actors.map((actor) => (
                  <div key={actor.id} className="rounded-xl border border-slate-200 p-4 dark:border-slate-800">
                    <div className="grid grid-cols-1 gap-3 lg:grid-cols-6">
                      <input
                        value={actor.callingCard?.name || actor.name}
                        disabled={readOnly}
                        onChange={(event) => setShowRelationships((prev) => ({
                          ...prev,
                          actors: prev.actors.map((item) => item.id === actor.id ? {
                            ...item,
                            callingCard: {
                              name: event.target.value,
                              role: item.callingCard?.role || "Actor",
                              bioShort: item.callingCard?.bioShort || null,
                              imageUrl: item.callingCard?.imageUrl || item.imageUrl || null,
                              imageStoragePath: item.callingCard?.imageStoragePath || item.imageStoragePath || null,
                              approvalStatus: item.callingCard?.approvalStatus || "draft",
                              visibility: item.callingCard?.visibility || "hidden",
                              updatedAt: new Date().toISOString(),
                            }
                          } : item)
                        }))}
                        placeholder="Calling card name"
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                      />
                      <select
                        value={actor.profileManagementType || "production_managed"}
                        disabled={readOnly}
                        onChange={(event) => setShowRelationships((prev) => ({
                          ...prev,
                          actors: prev.actors.map((item) => item.id === actor.id ? { ...item, profileManagementType: event.target.value as Actor["profileManagementType"] } : item)
                        }))}
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                      >
                        <option value="production_managed">Production managed</option>
                        <option value="agent_managed">Agent managed</option>
                      </select>
                      <input
                        value={actor.callingCard?.role || ""}
                        disabled={readOnly}
                        onChange={(event) => setShowRelationships((prev) => ({
                          ...prev,
                          actors: prev.actors.map((item) => item.id === actor.id ? {
                            ...item,
                            callingCard: {
                              name: item.callingCard?.name || item.name,
                              role: event.target.value,
                              bioShort: item.callingCard?.bioShort || null,
                              imageUrl: item.callingCard?.imageUrl || item.imageUrl || null,
                              imageStoragePath: item.callingCard?.imageStoragePath || item.imageStoragePath || null,
                              approvalStatus: item.callingCard?.approvalStatus || "draft",
                              visibility: item.callingCard?.visibility || "hidden",
                              updatedAt: new Date().toISOString(),
                            }
                          } : item)
                        }))}
                        placeholder="Role / character"
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                      />
                      <select
                        value={actor.callingCard?.approvalStatus || "draft"}
                        disabled={readOnly}
                        onChange={(event) => setShowRelationships((prev) => ({
                          ...prev,
                          actors: prev.actors.map((item) => item.id === actor.id ? {
                            ...item,
                            callingCard: {
                              name: item.callingCard?.name || item.name,
                              role: item.callingCard?.role || "Actor",
                              bioShort: item.callingCard?.bioShort || null,
                              imageUrl: item.callingCard?.imageUrl || item.imageUrl || null,
                              imageStoragePath: item.callingCard?.imageStoragePath || item.imageStoragePath || null,
                              approvalStatus: event.target.value as any,
                              visibility: item.callingCard?.visibility || "hidden",
                              updatedAt: new Date().toISOString(),
                            }
                          } : item)
                        }))}
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                      >
                        <option value="draft">Draft</option>
                        <option value="pending_review">Pending review</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                      <select
                        value={actor.callingCard?.visibility || "hidden"}
                        disabled={readOnly}
                        onChange={(event) => setShowRelationships((prev) => ({
                          ...prev,
                          actors: prev.actors.map((item) => item.id === actor.id ? {
                            ...item,
                            callingCard: {
                              name: item.callingCard?.name || item.name,
                              role: item.callingCard?.role || "Actor",
                              bioShort: item.callingCard?.bioShort || null,
                              imageUrl: item.callingCard?.imageUrl || item.imageUrl || null,
                              imageStoragePath: item.callingCard?.imageStoragePath || item.imageStoragePath || null,
                              approvalStatus: item.callingCard?.approvalStatus || "draft",
                              visibility: event.target.value as any,
                              updatedAt: new Date().toISOString(),
                            }
                          } : item)
                        }))}
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                      >
                        <option value="hidden">Hidden</option>
                        <option value="project_team">Project team</option>
                        <option value="internal">Internal</option>
                        <option value="public">Public</option>
                      </select>
                      <button
                        type="button"
                        disabled={readOnly}
                        onClick={() => handleSaveActor(actor)}
                        className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900"
                      >
                        Save card
                      </button>
                    </div>
                    <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-3">
                      <input
                        value={actor.callingCard?.imageUrl || actor.imageUrl || ""}
                        disabled={readOnly}
                        onChange={(event) => setShowRelationships((prev) => ({
                          ...prev,
                          actors: prev.actors.map((item) => item.id === actor.id ? {
                            ...item,
                            callingCard: {
                              name: item.callingCard?.name || item.name,
                              role: item.callingCard?.role || "Actor",
                              bioShort: item.callingCard?.bioShort || null,
                              imageUrl: event.target.value,
                              imageStoragePath: item.callingCard?.imageStoragePath || item.imageStoragePath || null,
                              approvalStatus: item.callingCard?.approvalStatus || "draft",
                              visibility: item.callingCard?.visibility || "hidden",
                              updatedAt: new Date().toISOString(),
                            }
                          } : item)
                        }))}
                        placeholder="Approved image URL or uploaded asset URL"
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                      />
                      <input
                        value={actor.representation?.representationName || ""}
                        disabled={readOnly}
                        onChange={(event) => setShowRelationships((prev) => ({
                          ...prev,
                          actors: prev.actors.map((item) => item.id === actor.id ? {
                            ...item,
                            representation: { ...(item.representation || {}), representationName: event.target.value }
                          } : item)
                        }))}
                        placeholder="Representative / agent"
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                      />
                      <input
                        value={actor.representation?.representationCompany || ""}
                        disabled={readOnly}
                        onChange={(event) => setShowRelationships((prev) => ({
                          ...prev,
                          actors: prev.actors.map((item) => item.id === actor.id ? {
                            ...item,
                            representation: { ...(item.representation || {}), representationCompany: event.target.value }
                          } : item)
                        }))}
                        placeholder="Representation company"
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                      />
                    </div>
                    <textarea
                      value={actor.callingCard?.bioShort || ""}
                      disabled={readOnly}
                      onChange={(event) => setShowRelationships((prev) => ({
                        ...prev,
                        actors: prev.actors.map((item) => item.id === actor.id ? {
                          ...item,
                          callingCard: {
                            name: item.callingCard?.name || item.name,
                            role: item.callingCard?.role || "Actor",
                            bioShort: event.target.value,
                            imageUrl: item.callingCard?.imageUrl || item.imageUrl || null,
                            imageStoragePath: item.callingCard?.imageStoragePath || item.imageStoragePath || null,
                            approvalStatus: item.callingCard?.approvalStatus || "draft",
                            visibility: item.callingCard?.visibility || "hidden",
                            updatedAt: new Date().toISOString(),
                          }
                        } : item)
                      }))}
                      placeholder="Short approved bio. Do not paste copyrighted profiles."
                      className="mt-3 h-20 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                    />
                  </div>
                ))}
                {showRelationships.actors.length === 0 && (
                  <div className="rounded-xl border border-dashed border-slate-300 px-5 py-8 text-center text-sm text-slate-500 dark:border-slate-700">
                    Link an actor to a character first, then curate their calling card here.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'productionTeam' && (
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Team</h3>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Project team links use People / HR only. No duplicate person records are created here.</p>
              <div className="mt-5 grid grid-cols-1 gap-3 lg:grid-cols-5">
                <div className="relative lg:col-span-1" ref={teamPersonAutocompleteRef}>
                  <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Name</label>
                  <input
                    value={teamPersonQuery}
                    disabled={readOnly}
                    onChange={(event) => {
                      setTeamPersonQuery(event.target.value);
                      setNewTeamMember((prev) => ({ ...prev, internalPersonId: "" }));
                      setShowTeamPersonSuggestions(true);
                    }}
                    onFocus={() => setShowTeamPersonSuggestions(true)}
                    onKeyDown={(event) => {
                      if (event.key === "Tab" && teamPersonSuggestions.length > 0 && !newTeamMember.internalPersonId) {
                        event.preventDefault();
                        selectTeamPerson(teamPersonSuggestions[0]);
                      }
                    }}
                    placeholder="Search People / HR..."
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#ffcc01] focus:ring-2 focus:ring-[#ffcc01]/30 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                  />
                  {showTeamPersonSuggestions && !readOnly && (
                    <div className="absolute z-30 mt-1 max-h-72 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900">
                      {teamPersonSuggestions.map((person) => (
                        <button
                          key={person.id}
                          type="button"
                          onClick={() => selectTeamPerson(person)}
                          className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-800"
                        >
                          {person.photoUrl ? (
                            <img src={person.photoUrl} alt="" className="h-8 w-8 rounded-md object-cover" referrerPolicy="no-referrer" />
                          ) : (
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-slate-100 text-xs font-bold text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                              {(person.name || person.email || "?").charAt(0)}
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{person.name}</div>
                            <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                              {[person.roleTitle || person.title || person.role, person.department].filter(Boolean).join(" · ") || person.email || "People / HR"}
                            </div>
                          </div>
                        </button>
                      ))}
                      {loadingTeamPeople ? (
                        <div className="flex items-center justify-center gap-2 px-4 py-3 text-center text-sm text-slate-500 dark:text-slate-400">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Loading People / HR records...
                        </div>
                      ) : teamPersonSuggestions.length === 0 ? (
                        <div className="px-4 py-3 text-center text-sm text-slate-500 dark:text-slate-400">No matching People / HR records found</div>
                      ) : null}
                    </div>
                  )}
                </div>
                <div>
                  <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Job Title</label>
                  <input value={newTeamMember.roleTitle} disabled={readOnly} onChange={(event) => setNewTeamMember((prev) => ({ ...prev, roleTitle: event.target.value }))} placeholder="Job title" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100" />
                </div>
                <div>
                  <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Department</label>
                  <input value={newTeamMember.department} disabled={readOnly} onChange={(event) => setNewTeamMember((prev) => ({ ...prev, department: event.target.value }))} placeholder="Department" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100" />
                </div>
                <div>
                  <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Group</label>
                  <select value={newTeamMember.displayGroup} disabled={readOnly} onChange={(event) => setNewTeamMember((prev) => ({ ...prev, displayGroup: event.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100">
                    <option value="leadership">Leadership</option>
                    <option value="leads">Leads</option>
                    <option value="team">Team</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button type="button" onClick={handleAddProductionTeamMember} disabled={readOnly || !newTeamMember.internalPersonId} className="w-full rounded-lg bg-[#ffcc01] px-3 py-2 text-sm font-semibold text-slate-900 disabled:opacity-50">Link team member</button>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
              {showRelationships.internalTeamMembers.map((member) => {
                const person = teamPeople.find((item) => item.id === member.internalPersonId)
                  || domainUsers.find((item) => item.id === member.internalPersonId);
                return (
                  <div key={member.id} className="flex items-center justify-between gap-4 border-b border-slate-100 px-5 py-4 last:border-b-0 dark:border-slate-800">
                    <div className="flex min-w-0 items-center gap-3">
                      {person?.photoUrl ? (
                        <img
                          src={person.photoUrl}
                          alt=""
                          className="h-11 w-11 shrink-0 rounded-md object-cover"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-slate-100 text-sm font-bold text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                          {getPersonInitials(person, member.internalPersonId)}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="truncate font-semibold text-slate-900 dark:text-slate-100">
                          {getPersonName(person) || member.internalPersonId}
                        </div>
                        <div className="truncate text-sm text-slate-500 dark:text-slate-400">
                          {member.roleTitle || "No role"} · {member.department || "No department"}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="sr-only" htmlFor={`team-group-${member.id}`}>Group</label>
                      <select
                        id={`team-group-${member.id}`}
                        value={member.displayGroup || "team"}
                        disabled={readOnly}
                        onChange={(event) => handleUpdateProductionTeamMember(member, { displayGroup: event.target.value as ProjectInternalTeamMember["displayGroup"] })}
                        className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm transition-colors hover:border-slate-300 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                        title="Project team group"
                      >
                        <option value="leadership">{getDisplayGroupLabel("leadership")}</option>
                        <option value="leads">{getDisplayGroupLabel("leads")}</option>
                        <option value="team">{getDisplayGroupLabel("team")}</option>
                      </select>
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-600 dark:bg-slate-800 dark:text-slate-300">{member.isActive ? "Active" : "Inactive"}</span>
                      <button
                        type="button"
                        disabled={readOnly}
                        onClick={() => deleteRelationship(`internal-team/${member.id}`)}
                        className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50 dark:hover:bg-red-950/40"
                        title="Remove project team link"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
              {showRelationships.internalTeamMembers.length === 0 && (
                <div className="px-5 py-8 text-center text-slate-500">
                  {isLoadingRelationships ? "Loading project team members..." : "No project team members linked yet."}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'crew' && (
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Crew</h3>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">External crew links use CRM / External People. New names are created there first, then linked to this project.</p>
              <div className="mt-5 grid grid-cols-1 gap-3 lg:grid-cols-5">
                <select value={newCrewMember.externalPersonId} disabled={readOnly} onChange={(event) => {
                  const person = externalPeople.find((item) => item.id === event.target.value);
                  setNewCrewMember((prev) => ({
                    ...prev,
                    externalPersonId: event.target.value,
                    name: person?.name || "",
                    roleTitle: prev.roleTitle || person?.roleTitle || person?.title || "",
                    department: prev.department || person?.department || "",
                  }));
                }} className="rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100">
                  <option value="">Create or select CRM person</option>
                  {externalPeople.map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}
                </select>
                <input value={newCrewMember.name} disabled={readOnly || Boolean(newCrewMember.externalPersonId)} onChange={(event) => setNewCrewMember((prev) => ({ ...prev, name: event.target.value }))} placeholder="New CRM person name" className="rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100" />
                <input value={newCrewMember.roleTitle} disabled={readOnly} onChange={(event) => setNewCrewMember((prev) => ({ ...prev, roleTitle: event.target.value }))} placeholder="Role title" className="rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100" />
                <input value={newCrewMember.department} disabled={readOnly} onChange={(event) => setNewCrewMember((prev) => ({ ...prev, department: event.target.value }))} placeholder="Department" className="rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100" />
                <button type="button" onClick={handleAddExternalCrewMember} disabled={readOnly || (!newCrewMember.externalPersonId && !newCrewMember.name.trim())} className="rounded-lg bg-[#ffcc01] px-3 py-2 text-sm font-semibold text-slate-900 disabled:opacity-50">Link crew</button>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
              {showRelationships.externalCrewMembers.map((member) => {
                const person = externalPeople.find((item) => item.id === member.externalPersonId);
                return (
                  <div key={member.id} className="border-b border-slate-100 px-5 py-4 last:border-b-0 dark:border-slate-800">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <div className="font-semibold text-slate-900 dark:text-slate-100">{member.roleTitle || "Crew"} - {person?.name || member.externalPersonId}</div>
                        <div className="text-sm text-slate-500 dark:text-slate-400">{member.department || "No department"} · CRM / External People source</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-600 dark:bg-slate-800 dark:text-slate-300">{member.isActive ? "Active" : "Inactive"}</span>
                        <button
                          type="button"
                          disabled={readOnly}
                          onClick={() => deleteRelationship(`external-crew/${member.id}`)}
                          className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50 dark:hover:bg-red-950/40"
                          title="Remove project crew link"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-6">
                      <select
                        value={member.profileManagementType || "production_managed"}
                        disabled={readOnly}
                        onChange={(event) => setShowRelationships((prev) => ({
                          ...prev,
                          externalCrewMembers: prev.externalCrewMembers.map((item) => item.id === member.id ? { ...item, profileManagementType: event.target.value as ProjectExternalCrewMember["profileManagementType"] } : item)
                        }))}
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                      >
                        <option value="production_managed">Production managed</option>
                        <option value="crew_self_claim_ready">Self-claim ready</option>
                        <option value="self_claimed">Self-claimed</option>
                      </select>
                      <select
                        value={member.availabilityStatus || "unknown"}
                        disabled={readOnly}
                        onChange={(event) => setShowRelationships((prev) => ({
                          ...prev,
                          externalCrewMembers: prev.externalCrewMembers.map((item) => item.id === member.id ? { ...item, availabilityStatus: event.target.value as ProjectExternalCrewMember["availabilityStatus"] } : item)
                        }))}
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                      >
                        <option value="unknown">Availability unknown</option>
                        <option value="available">Available</option>
                        <option value="limited">Limited</option>
                        <option value="unavailable">Unavailable</option>
                      </select>
                      <input
                        value={member.cvUrl || ""}
                        disabled={readOnly}
                        onChange={(event) => setShowRelationships((prev) => ({
                          ...prev,
                          externalCrewMembers: prev.externalCrewMembers.map((item) => item.id === member.id ? { ...item, cvUrl: event.target.value } : item)
                        }))}
                        placeholder="CV URL"
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                      />
                      <select
                        value={member.callingCard?.approvalStatus || "draft"}
                        disabled={readOnly}
                        onChange={(event) => setShowRelationships((prev) => ({
                          ...prev,
                          externalCrewMembers: prev.externalCrewMembers.map((item) => item.id === member.id ? {
                            ...item,
                            callingCard: {
                              name: item.callingCard?.name || person?.name || "Crew",
                              role: item.callingCard?.role || item.roleTitle || null,
                              bioShort: item.callingCard?.bioShort || null,
                              imageUrl: item.callingCard?.imageUrl || null,
                              imageStoragePath: item.callingCard?.imageStoragePath || null,
                              approvalStatus: event.target.value as any,
                              visibility: item.callingCard?.visibility || "hidden",
                              updatedAt: new Date().toISOString(),
                            }
                          } : item)
                        }))}
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                      >
                        <option value="draft">Draft</option>
                        <option value="pending_review">Pending review</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                      <select
                        value={member.callingCard?.visibility || "hidden"}
                        disabled={readOnly}
                        onChange={(event) => setShowRelationships((prev) => ({
                          ...prev,
                          externalCrewMembers: prev.externalCrewMembers.map((item) => item.id === member.id ? {
                            ...item,
                            callingCard: {
                              name: item.callingCard?.name || person?.name || "Crew",
                              role: item.callingCard?.role || item.roleTitle || null,
                              bioShort: item.callingCard?.bioShort || null,
                              imageUrl: item.callingCard?.imageUrl || null,
                              imageStoragePath: item.callingCard?.imageStoragePath || null,
                              approvalStatus: item.callingCard?.approvalStatus || "draft",
                              visibility: event.target.value as any,
                              updatedAt: new Date().toISOString(),
                            }
                          } : item)
                        }))}
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                      >
                        <option value="hidden">Hidden</option>
                        <option value="project_team">Project team</option>
                        <option value="internal">Internal</option>
                        <option value="public">Public</option>
                      </select>
                      <button
                        type="button"
                        disabled={readOnly}
                        onClick={() => handleSaveExternalCrewMember(member)}
                        className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900"
                      >
                        Save card
                      </button>
                    </div>
                    <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-3">
                      <input
                        value={member.callingCard?.imageUrl || ""}
                        disabled={readOnly}
                        onChange={(event) => setShowRelationships((prev) => ({
                          ...prev,
                          externalCrewMembers: prev.externalCrewMembers.map((item) => item.id === member.id ? {
                            ...item,
                            callingCard: {
                              name: item.callingCard?.name || person?.name || "Crew",
                              role: item.callingCard?.role || item.roleTitle || null,
                              bioShort: item.callingCard?.bioShort || null,
                              imageUrl: event.target.value,
                              imageStoragePath: item.callingCard?.imageStoragePath || null,
                              approvalStatus: item.callingCard?.approvalStatus || "draft",
                              visibility: item.callingCard?.visibility || "hidden",
                              updatedAt: new Date().toISOString(),
                            }
                          } : item)
                        }))}
                        placeholder="Approved image URL or uploaded asset URL"
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                      />
                      <input
                        value={member.representation?.representationName || ""}
                        disabled={readOnly}
                        onChange={(event) => setShowRelationships((prev) => ({
                          ...prev,
                          externalCrewMembers: prev.externalCrewMembers.map((item) => item.id === member.id ? {
                            ...item,
                            representation: { ...(item.representation || {}), representationName: event.target.value }
                          } : item)
                        }))}
                        placeholder="Representative"
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                      />
                      <input
                        value={member.representation?.representationCompany || ""}
                        disabled={readOnly}
                        onChange={(event) => setShowRelationships((prev) => ({
                          ...prev,
                          externalCrewMembers: prev.externalCrewMembers.map((item) => item.id === member.id ? {
                            ...item,
                            representation: { ...(item.representation || {}), representationCompany: event.target.value }
                          } : item)
                        }))}
                        placeholder="Representation company"
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                      />
                    </div>
                    <textarea
                      value={member.callingCard?.bioShort || ""}
                      disabled={readOnly}
                      onChange={(event) => setShowRelationships((prev) => ({
                        ...prev,
                        externalCrewMembers: prev.externalCrewMembers.map((item) => item.id === member.id ? {
                          ...item,
                          callingCard: {
                            name: item.callingCard?.name || person?.name || "Crew",
                            role: item.callingCard?.role || item.roleTitle || null,
                            bioShort: event.target.value,
                            imageUrl: item.callingCard?.imageUrl || null,
                            imageStoragePath: item.callingCard?.imageStoragePath || null,
                            approvalStatus: item.callingCard?.approvalStatus || "draft",
                            visibility: item.callingCard?.visibility || "hidden",
                            updatedAt: new Date().toISOString(),
                          }
                        } : item)
                      }))}
                      placeholder="Short approved crew bio. Keep contact, rate, and HR details out of this public-facing card."
                      className="mt-3 h-20 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                    />
                  </div>
                );
              })}
              {showRelationships.externalCrewMembers.length === 0 && <div className="px-5 py-8 text-center text-slate-500">No external crew linked yet.</div>}
            </div>
          </div>
        )}

        {activeTab === 'integrations' && (
          <div className="space-y-6">
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white shadow-sm">
                    <img src="https://www.gstatic.com/images/branding/product/1x/drive_48dp.png" alt="Google Drive" className="h-7 w-7" referrerPolicy="no-referrer" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">Google Drive</h3>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      {driveSummaryText}
                    </p>
                  </div>
                </div>
                {driveConnected ? (
                  <span className="inline-flex items-center gap-1 rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Connected
                  </span>
                ) : (
                  <button
                    onClick={handleConnectGoogleDrive}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50"
                  >
                    <img src="https://www.gstatic.com/images/branding/product/1x/drive_48dp.png" alt="Drive" className="h-4 w-4" referrerPolicy="no-referrer" />
                    Connect Google Drive
                  </button>
                )}
              </div>

              {driveConnected && (
                <div className="mt-5 space-y-4">
                  {!googleDriveLinked ? (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-900">Create or link a project drive</p>
                          <p className="mt-1 text-xs text-slate-500">
                            Keep the project drive setup here instead of the new-project flow.
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            onClick={() => void onSetupDrive()}
                            disabled={isSettingUpDrive || readOnly}
                            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {isSettingUpDrive ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                            {isSettingUpDrive ? "Creating..." : "Create Shared Drive"}
                          </button>
                          <select
                            value={selectedDriveId}
                            onChange={(event) => setSelectedDriveId(event.target.value)}
                            disabled={isSettingUpDrive || readOnly}
                            className="min-w-[200px] rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs text-slate-700 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {sharedDrives.length === 0 && <option value="">My Drive (Personal)</option>}
                            {sharedDrives.map((drive) => (
                              <option key={drive.id} value={drive.id}>
                                {drive.name}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={() => void onLinkDrive(selectedDriveId, sharedDrives.find((drive) => drive.id === selectedDriveId)?.name)}
                            disabled={isSettingUpDrive || readOnly}
                            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <ExternalLink className="h-4 w-4" />
                            Link Existing
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_280px]">
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <p className="text-sm font-medium text-slate-900">Linked Google Drive</p>
                        <p className="mt-1 text-xs text-slate-500">
                          {setup.driveFolderName || "Shared Drive"}{setup.driveFolderId ? ` · ${setup.driveFolderId}` : ""}
                        </p>
                        <p className="mt-2 text-[11px] text-slate-500">
                          The folder link now lives here so new projects and imported legacy projects use the same integration surface.
                        </p>
                      </div>
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <p className="text-sm font-medium text-slate-900">Thumbnail sync</p>
                        <div className="mt-3 flex items-center gap-3">
                          <div className="h-14 w-14 shrink-0 overflow-hidden rounded border border-slate-200 bg-slate-100">
                            {setup.thumbnail ? (
                              <img src={setup.thumbnail} alt="Thumbnail" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center text-slate-400">
                                <Plus className="h-5 w-5" />
                              </div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-xs font-medium text-slate-900 dark:text-slate-100">Project thumbnail</p>
                            <p className="text-[10px] text-slate-500">Square artwork syncs best to Drive.</p>
                          </div>
                          <button
                            onClick={async () => {
                              if (!setup.driveFolderId || !setup.thumbnail || isSyncing) return;
                              setIsSyncing(true);
                              try {
                                const res = await fetch('/api/drive/update-shared-drive', {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    'x-user-uid': user?.uid || ""
                                  },
                                  body: JSON.stringify({
                                    driveId: setup.driveFolderId,
                                    thumbnail: setup.thumbnail
                                  })
                                });
                                if (!res.ok) {
                                  const data = await res.json();
                                  throw new Error(data.details || data.error || 'Failed to sync');
                                }
                                toast.success('Thumbnail synced to Google Drive');
                              } catch (err: any) {
                                toast.error(err.message || 'Failed to sync thumbnail');
                              } finally {
                                setIsSyncing(false);
                              }
                            }}
                            disabled={isSyncing}
                            className={cn(
                              "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-medium transition-colors",
                              isSyncing
                                ? "cursor-not-allowed bg-slate-100 text-slate-500"
                                : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                            )}
                          >
                            {isSyncing ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                            {isSyncing ? 'Syncing...' : 'Sync now'}
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end">
                      {!isDisconnectDriveConfirmOpen ? (
                        <button
                          type="button"
                          disabled={readOnly}
                          onClick={() => setIsDisconnectDriveConfirmOpen(true)}
                          className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 text-xs font-semibold text-red-600 transition-colors hover:border-red-300 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-900/60 dark:bg-slate-950 dark:text-red-300 dark:hover:bg-red-950/30"
                        >
                          Disconnect Drive
                        </button>
                      ) : (
                        <div className="flex flex-col items-end gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-right dark:border-red-900/60 dark:bg-red-950/20">
                          <div>
                            <p className="text-xs font-semibold text-red-700 dark:text-red-200">Disconnect this project from Google Drive?</p>
                            <p className="mt-1 max-w-md text-[11px] text-red-600 dark:text-red-300">
                              This only removes the linked Drive from this project. It will not delete files or disconnect your Google account.
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setIsDisconnectDriveConfirmOpen(false)}
                              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={handleDisconnectProjectDrive}
                              className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-red-700"
                            >
                              Yes, disconnect
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white shadow-sm">
                    <img src="/fpt-icon.svg" alt="Autodesk Flow Production Tracking" className="h-8 w-8" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">Autodesk Flow Production Tracking</h3>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{fptSummaryText}</p>
                  </div>
                </div>
                {fptLinked ? (
                  <span className="inline-flex items-center gap-1 rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Linked
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                    Not linked
                  </span>
                )}
              </div>

              <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                {fptLinked ? (
                  <>
                    <p className="text-sm font-medium text-slate-900">Next best move</p>
                    <p className="mt-1 text-xs text-slate-500">
                      Pull latest from the ShotGrid tab first so the app ingests live FPT statuses, timelogs, and profitability safely before any further sync action.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-slate-500">
                      {projectId ? <span>Local project: {projectId}</span> : null}
                      {fptProjectId ? <span>Flow Production Tracking project: {fptProjectId}</span> : null}
                      {lastPulledAt ? <span>Last pulled: {new Date(lastPulledAt).toLocaleString()}</span> : <span>No Flow Production Tracking pull has been run yet.</span>}
                    </div>
                    {shouldPromptInitialRatePush ? (
                      <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div className="min-w-0 flex-1">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-700">
                              First Link Follow-Up
                            </p>
                            <p className="mt-1 text-sm font-semibold text-slate-900">Push task rates to Flow Production Tracking</p>
                            <p className="mt-1 text-[11px] text-amber-900">
                              `sg_rate` should come from the app at task level. Flow Production Tracking then calculates `sg_bid_cost` nightly from `est_in_mins × sg_rate`. `sg_person_cost` and `sg_profit` stay Flow Production Tracking-calculated and should not be written from the app.
                            </p>
                          </div>
                          <button
                            onClick={handleSyncTaskRatesToFpt}
                            disabled={isSyncingTaskRates}
                            className={cn(
                              "inline-flex items-center justify-center gap-2 rounded-full px-3 py-2 text-xs font-semibold transition-colors",
                              isSyncingTaskRates
                                ? "cursor-not-allowed bg-slate-100 text-slate-400"
                                : "bg-amber-600 text-white hover:bg-amber-700"
                            )}
                          >
                            {isSyncingTaskRates ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Calculator className="h-3.5 w-3.5" />}
                            {isSyncingTaskRates ? "Syncing rates..." : "Push task rates now"}
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </>
                ) : (
                  <>
                    <p className="text-sm font-medium text-slate-900">Connection path</p>
                    <p className="mt-1 text-xs text-slate-500">
                      For app-born projects, create and push from ShotGrid. For imported legacy bids, connect to the existing FPT project first and avoid writing anything on the first link.
                    </p>
                    <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
                      {isLoadingFptSuggestion ? (
                        <div className="mb-4 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          Checking held FPT project history for a likely project match...
                        </div>
                      ) : null}

                      {fptSuggestedProject ? (
                        <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="min-w-0 flex-1">
                              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700">
                                Suggested Existing FPT Project
                              </p>
                              <p className="mt-1 truncate text-sm font-semibold text-slate-900">{fptSuggestedProject.name}</p>
                              <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-slate-600">
                                <span>Flow Production Tracking id: {fptSuggestedProject.id}</span>
                                {fptSuggestedProject.status ? <span>Status: {fptSuggestedProject.status}</span> : null}
                                {typeof fptSuggestedProject.heldTimelogCount === "number" ? (
                                  <span>{fptSuggestedProject.heldTimelogCount} held timelog{fptSuggestedProject.heldTimelogCount === 1 ? "" : "s"}</span>
                                ) : null}
                                {fptSuggestedProject.matchType ? (
                                  <span>
                                    Match: {fptSuggestedProject.matchType === "exact_name" ? "exact project name" : "normalized project name"}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {fptSuggestedProject.url ? (
                                <a
                                  href={fptSuggestedProject.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-white px-3 py-1.5 text-[11px] font-medium text-emerald-700 hover:bg-emerald-100"
                                >
                                  <ExternalLink className="h-3.5 w-3.5" />
                                  Open
                                </a>
                              ) : null}
                              <button
                                onClick={() => handleLinkExistingFptProject(fptSuggestedProject)}
                                disabled={isLinkingFpt}
                                className={cn(
                                  "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold transition-colors",
                                  isLinkingFpt
                                    ? "cursor-not-allowed bg-slate-100 text-slate-400"
                                    : "bg-emerald-600 text-white hover:bg-emerald-700"
                                )}
                              >
                                {isLinkingFpt ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                                {isLinkingFpt ? "Linking..." : "Link suggested project"}
                              </button>
                            </div>
                          </div>
                          <p className="mt-2 text-[11px] text-emerald-800">
                            This suggestion is conservative and comes from held FPT project history. First link remains metadata-only, so you can confirm it safely before using sync actions.
                          </p>
                        </div>
                      ) : null}

                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                        <div className="min-w-0 flex-1">
                          <label className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                            Link Existing FPT Project
                          </label>
                          <input
                            type="text"
                            value={fptSearchQuery}
                            onChange={(event) => setFptSearchQuery(event.target.value)}
                            placeholder="Search by project name or show code"
                            className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-400"
                          />
                        </div>
                        <button
                          onClick={handleSearchFptProjects}
                          disabled={isSearchingFpt || isLinkingFpt || fptSearchQuery.trim().length < 2}
                          className={cn(
                            "inline-flex items-center justify-center gap-2 rounded-full px-3 py-2 text-xs font-semibold transition-colors",
                            isSearchingFpt || isLinkingFpt || fptSearchQuery.trim().length < 2
                              ? "cursor-not-allowed bg-slate-100 text-slate-400"
                              : "bg-slate-900 text-white hover:bg-slate-800"
                          )}
                        >
                          {isSearchingFpt ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Database className="h-3.5 w-3.5" />}
                          {isSearchingFpt ? "Searching..." : "Search Flow Production Tracking"}
                        </button>
                      </div>
                      <p className="mt-3 text-[11px] text-slate-500">
                        First link is metadata-only. We store the project connection in the app and do not create or update anything in FPT until you choose a later sync action.
                      </p>

                      {fptSearchResults.length > 0 ? (
                        <div className="mt-4 space-y-2">
                          {fptSearchResults.map((candidate) => (
                            <div
                              key={candidate.id}
                              className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:flex-row sm:items-center sm:justify-between"
                            >
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-semibold text-slate-900">{candidate.name}</p>
                                <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-slate-500">
                                  <span>Flow Production Tracking id: {candidate.id}</span>
                                  {candidate.showCode ? <span>Show code: {candidate.showCode}</span> : null}
                                  {candidate.status ? <span>Status: {candidate.status}</span> : null}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {candidate.url ? (
                                  <a
                                    href={candidate.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-medium text-slate-600 hover:bg-slate-100"
                                  >
                                    <ExternalLink className="h-3.5 w-3.5" />
                                    Open
                                  </a>
                                ) : null}
                                <button
                                  onClick={() => handleLinkExistingFptProject(candidate)}
                                  disabled={isLinkingFpt}
                                  className={cn(
                                    "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold transition-colors",
                                    isLinkingFpt
                                      ? "cursor-not-allowed bg-slate-100 text-slate-400"
                                      : "bg-emerald-600 text-white hover:bg-emerald-700"
                                  )}
                                >
                                  {isLinkingFpt ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                                  {isLinkingFpt ? "Linking..." : "Link existing project"}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

      {activeTab === "shotcodes" && (
          <div className="flex-1 min-h-0 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
            <ShotCodeGenerator
              isOpen={true}
              onClose={() => {}}
              setup={setup}
              shots={shots}
              isEmbedded={true}
              onGenerate={(newShots, updateExisting, newShowCode) => {
                let updatedShots = [...shots];
                
                if (updateExisting) {
                  updatedShots = updatedShots.map(shot => {
                    const parts = shot.shotCode.split('_');
                    let epStr, seqStr, shotStr;
                    
                    if (parts.length === 4) {
                      epStr = parts[1];
                      seqStr = parts[2];
                      shotStr = parts[3];
                    } else if (parts.length === 3) {
                      epStr = parts[0];
                      seqStr = parts[1];
                      shotStr = parts[2];
                    } else {
                      return shot;
                    }
                    
                    const finalShowCode = newShowCode !== undefined ? newShowCode : setup.showCode;
                    const newShotCode = finalShowCode 
                      ? `${finalShowCode}_${epStr}_${seqStr}_${shotStr}`
                      : `${epStr}_${seqStr}_${shotStr}`;
                      
                    return { ...shot, shotCode: newShotCode };
                    });
                  }
                  
                  const existingCodes = new Set(updatedShots.map(s => s.shotCode));
                  const uniqueNewShots = newShots.filter(s => !existingCodes.has(s.shotCode));
                  
                  onShotsChange(sortShots([...updatedShots, ...uniqueNewShots]));
                  toast.success("Shot codes generated successfully");
                }}
                onUpdateSetup={onSetupChange}
              />
            </div>
          )}
      </div>
    </div>
  );
}
